import React, { useState } from 'react';
import { useHoverCapable } from '../../hooks/useHoverCapable';

// Navbar + NavDropdown — react-bootstrap-compatible top nav ("operations control bar").
// Responsive: single row on lg+; below lg the Toggle (hamburger) shows/hides a stacked
// menu. Layout for the stacked mobile menu is in tailwind.css (@media, .wrap-collapse).
type DivProps = React.HTMLAttributes<HTMLDivElement>;

const NavbarCtx = React.createContext<{ expanded: boolean; toggle: () => void }>({ expanded: false, toggle: () => {} });

interface NavbarType extends React.FC<
  React.HTMLAttributes<HTMLElement> & { bg?: string; variant?: string; expand?: string; fixed?: 'top' | 'bottom' }
> {
  Brand: React.FC<DivProps>;
  Toggle: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { 'aria-controls'?: string }>;
  Collapse: React.FC<DivProps & { id?: string }>;
}

export const Navbar = (({ bg, variant, expand, fixed, className = '', children, ...rest }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <NavbarCtx.Provider value={{ expanded, toggle: () => setExpanded((e) => !e) }}>
      <nav
        className={`wrap-controlbar tw-flex tw-flex-wrap tw-items-center tw-gap-x-1 tw-min-h-[3.25rem] tw-bg-ink tw-px-4 tw-text-white tw-shadow-md tw-border-b tw-border-ink-line ${
          fixed === 'top' ? 'tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-z-[1030]' : fixed === 'bottom' ? 'tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[1030]' : ''
        } ${className}`}
        {...rest}
      >
        {children}
      </nav>
    </NavbarCtx.Provider>
  );
}) as NavbarType;

Navbar.Brand = ({ className = '', children, ...rest }) => (
  <div className={`tw-flex tw-shrink-0 tw-items-center tw-font-display tw-text-lg tw-font-semibold tw-tracking-tight tw-text-white ${className}`} {...rest}>{children}</div>
);

Navbar.Toggle = ({ className = '', onClick, ...rest }) => {
  const { expanded, toggle } = React.useContext(NavbarCtx);
  return (
    <button
      type="button"
      aria-label="Toggle navigation"
      aria-expanded={expanded}
      onClick={(e) => { onClick?.(e); toggle(); }}
      className={`tw-ml-auto tw-rounded tw-border tw-border-white/25 tw-px-2.5 tw-py-1.5 tw-text-white/90 hover:tw-bg-white/10 lg:tw-hidden ${className}`}
      {...rest}
    >
      <i className={`bi ${expanded ? 'bi-x-lg' : 'bi-list'} tw-text-xl`} />
    </button>
  );
};

Navbar.Collapse = ({ id, className = '', children, ...rest }) => {
  const { expanded } = React.useContext(NavbarCtx);
  return (
    <div
      id={id}
      className={`wrap-collapse tw-w-full tw-flex-col tw-items-stretch tw-gap-1 lg:tw-flex lg:tw-w-auto lg:tw-flex-1 lg:tw-flex-row lg:tw-items-center ${expanded ? 'tw-flex' : 'tw-hidden'} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
};

// NavDropdown — hover-opened on a mouse, tap-toggled on touch. Item forwards ref + `as`.
//
// It used to bind `onMouseEnter`/`onMouseLeave` unconditionally alongside a click toggle on the
// button. On a touch device a tap synthesises `mouseover` (which opened it) and then delivers
// the real `click` (which toggled it shut), so reaching a page took two taps; and because
// `mouseleave` never fires from a finger, the menu stayed open after navigating. Both were
// reported on iPad. Hover is now bound only where hover actually exists, and the menu closes
// when an item is chosen, when a tap lands outside it, or on Escape.
interface NavDropdownType extends React.FC<
  React.HTMLAttributes<HTMLDivElement> & { title?: React.ReactNode; id?: string }
> {
  Item: React.ForwardRefExoticComponent<
    React.HTMLAttributes<HTMLElement> & { as?: React.ElementType; to?: string; disabled?: boolean } & React.RefAttributes<HTMLElement>
  >;
  Divider: React.FC;
}

export const NavDropdown = (({ title, id, className = '', children, ...rest }) => {
  const [open, setOpen] = useState(false);
  const hoverCapable = useHoverCapable();
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Close on a tap/click outside, and on Escape. Without this a touch user has no gesture that
  // dismisses the menu at all — there is no `mouseleave` to rely on.
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent | MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const hoverHandlers = hoverCapable
    ? { onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false) }
    : {};

  return (
    <div ref={rootRef} className={`wrap-navdropdown tw-relative ${className}`} {...hoverHandlers} {...rest}>
      <button
        type="button"
        id={id}
        aria-expanded={open}
        className={`tw-inline-flex tw-w-full tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-md tw-px-3 tw-py-2 tw-font-medium tw-transition hover:tw-bg-white/10 lg:tw-w-auto ${open ? 'tw-text-white tw-bg-white/10' : 'tw-text-white/90 hover:tw-text-white'}`}
        onClick={() => setOpen((o) => !o)}
      >
        {title}
        <i className={`bi bi-chevron-down tw-ml-auto tw-text-[0.7rem] tw-transition-transform tw-duration-150 lg:tw-ml-0 ${open ? 'tw-rotate-180' : ''}`} />
      </button>
      {open && (
        // Choosing anything inside the menu dismisses it. This is what stops the dropdown
        // hanging open over the page the user just navigated to.
        <div
          className="wrap-dropdown-menu tw-absolute tw-left-0 tw-top-full tw-z-[1040] tw-min-w-[13rem] tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-py-1.5 tw-text-content tw-shadow-lg"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}) as NavDropdownType;

NavDropdown.Item = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement> & { as?: React.ElementType; to?: string; disabled?: boolean }>(
  ({ as, className = '', children, ...rest }, ref) => {
    const Comp: React.ElementType = as || 'button';
    return (
      <Comp
        ref={ref}
        className={`tw-block tw-w-full tw-cursor-pointer tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-text-content tw-no-underline tw-transition hover:tw-bg-surface-hover ${className}`}
        {...rest}
      >
        {children}
      </Comp>
    );
  },
);
NavDropdown.Divider = () => <div className="tw-my-1 tw-border-t tw-border-line" />;

export default Navbar;
